/**
 * ============================================================
 * useNavPreferences.ts — Preferencias de Navegacao (Favoritos e Recentes)
 * ============================================================
 *
 * Responsabilidade: Gerenciar paginas favoritas e recentes do usuario,
 *   persistindo no Supabase (user_settings) com debounce de 1.5s.
 *   Migra automaticamente do localStorage para o DB na primeira carga.
 *
 * Retorno:
 *   - favorites: string[] — caminhos das paginas favoritas
 *   - recent: string[] — ultimas 5 paginas visitadas
 *   - toggleFavorite(path) — adiciona/remove favorito
 *   - addRecent(path) — registra visita a pagina
 *   - loaded: boolean — indica se os dados ja foram carregados
 *
 * Efeitos colaterais:
 *   - Carrega preferencias do DB ao montar
 *   - Salva no DB com debounce de 1500ms a cada alteracao
 *   - Usa merge para nao sobrescrever outras settings do usuario
 *
 * Quando usar: No Sidebar para exibir favoritos e paginas recentes.
 * ============================================================
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useStore } from '@/store/useStore';

const SETTINGS_KEY = 'nav_preferences';
const SAVE_DEBOUNCE = 1500;

interface NavPrefs {
  favorites: string[];
  recent: string[];
}

export function useNavPreferences() {
  const activeProjectId = useStore(s => s.activeProjectId);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [recent, setRecent] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settingsIdRef = useRef<string | null>(null);
  const userIdRef = useRef<string | null>(null);

  // Load from DB (or fallback to localStorage for migration)
  useEffect(() => {
    if (!activeProjectId) return;
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;
      userIdRef.current = user.id;

      const { data } = await supabase
        .from('user_settings')
        .select('id, settings')
        .eq('project_id', activeProjectId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (data?.settings && (data.settings as any)[SETTINGS_KEY]) {
        const prefs = (data.settings as any)[SETTINGS_KEY] as NavPrefs;
        setFavorites(prefs.favorites || []);
        setRecent(prefs.recent || []);
        settingsIdRef.current = data.id;
      } else {
        // Migrate from localStorage
        try {
          const lsFav = JSON.parse(localStorage.getItem('nav-favorites') || '[]');
          const lsRec = JSON.parse(localStorage.getItem('nav-recent') || '[]');
          setFavorites(lsFav);
          setRecent(lsRec);
        } catch { /* ignore */ }
        if (data) settingsIdRef.current = data.id;
      }
      setLoaded(true);
    };
    load();
  }, [activeProjectId]);

  // Debounced save to DB
  const saveToDb = useCallback((newFavs: string[], newRecent: string[]) => {
    if (!loaded || !activeProjectId || !userIdRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const prefs: NavPrefs = { favorites: newFavs, recent: newRecent };
      const userId = userIdRef.current!;

      // Get current settings to merge
      const { data: existing } = await supabase
        .from('user_settings')
        .select('id, settings')
        .eq('project_id', activeProjectId)
        .eq('user_id', userId)
        .maybeSingle();

      const merged = { ...(existing?.settings as any || {}), [SETTINGS_KEY]: prefs };

      await supabase.from('user_settings')
        .upsert(
          { project_id: activeProjectId, user_id: userId, settings: merged, updated_at: new Date().toISOString() } as any,
          { onConflict: 'project_id,user_id' }
        );
    }, SAVE_DEBOUNCE);
  }, [loaded, activeProjectId]);

  const toggleFavorite = useCallback((path: string) => {
    setFavorites(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      saveToDb(next, recent);
      return next;
    });
  }, [recent, saveToDb]);

  const addRecent = useCallback((path: string) => {
    // Ignore utility paths
    if (['/auth', '/workspaces'].includes(path)) return;
    setRecent(prev => {
      const next = [path, ...prev.filter(p => p !== path)].slice(0, 5);
      saveToDb(favorites, next);
      return next;
    });
  }, [favorites, saveToDb]);

  return { favorites, recent, toggleFavorite, addRecent, loaded };
}
